import { useCallback, useEffect, useRef, useState } from "react";

export interface BasePayload { generatedAt: string }

/**
 * Loads /api/data and exposes a Refresh that reports what it actually did,
 * rather than blinking and appearing to have done nothing when the data was
 * already current.
 */
export function useData<T extends BasePayload>() {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastRefresh, setLastRefresh] = useState<"updated" | "current" | null>(null);

  /**
   * Held in a ref, not read from `data`. Depending on `data` here would give
   * `load` a new identity on every payload, re-firing the mount effect below
   * and refetching forever.
   */
  const lastGeneratedAt = useRef<string | null>(null);
  const timer = useRef<number | null>(null);

  const load = useCallback(async (fresh = false) => {
    if (fresh) setRefreshing(true); else setLoading(true);
    setError(null);
    try {
      const res = await fetch(`/api/data${fresh ? "?refresh=1" : ""}`);
      const json = (await res.json()) as T & { error?: string };
      if (!res.ok) throw new Error(json?.error ?? `Request failed (${res.status})`);

      if (fresh) {
        const unchanged = lastGeneratedAt.current === json.generatedAt;
        setLastRefresh(unchanged ? "current" : "updated");
        if (timer.current) window.clearTimeout(timer.current);
        timer.current = window.setTimeout(() => setLastRefresh(null), 4000);
      }

      lastGeneratedAt.current = json.generatedAt;
      setData(json);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not load data");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    void load(false);
    return () => { if (timer.current) window.clearTimeout(timer.current); };
  }, [load]);

  return { data, error, loading, refreshing, lastRefresh, refresh: () => load(true) };
}
